//	------------------------	------------------------	------------------------
//	Description: Consult wait time for the booking consult page
//  Notes: This can be toggled on or off with app.site_status.wr_stat.use_new
//	------------------------	------------------------	------------------------

//	------------------------	------------------------	------------------------
//	Imports
//	------------------------	------------------------	------------------------

import React, { useState } from 'react'
import app from 'ampersand-app'
import { Modal } from 'semantic-ui-react'

import SIcon from '../../../NUI/StreamlineIcons/SIcon'
import Alert from '../../../NUI/Alert'
import WaitTimeConsult from './consult'
import { WaitTimeText } from './WaitTimeText'
import RecentDocsActivity from './recent_docs_activity'
import { WaitTimeInfoModal } from "./WaitTimeInfoModal";
import { useExperiment } from '@iscr/xAppLib/Hooks/useExperiment';
import { WAIT_TIME_EXP } from "@iscr/views/pages/homepage/consts";

//	------------------------	------------------------	------------------------
//	Globals
//	------------------------	------------------------	------------------------

const mins_max = 600 // This should match the BE update_historic, mins_max
const mins_min = 10  // This should match the BE update_historic, mins_min

// Cuts a transparent notch out of the badge's top-right corner for the info icon to sit in, so whatever
// is behind the badge shows through. Centred on the WaitTimeInfoModal trigger (38px, offset -16px).
const info_notch = "[mask-image:radial-gradient(circle_at_calc(100%_-_3px)_3px,transparent_18px,#000_18.5px)]"

function mins_to_hum_time(mins) {
	const hs = Math.floor(mins / 60)
	const ms = mins % 60
	return `${hs > 1 && `${hs} hours` || hs == 1 && '1 hour' || ''} ${ms > 1 && `${ms} mins` || ms == 1 && '1 minute' || ''}`
}

//	------------------------	------------------------	------------------------
//	Exports
//	------------------------	------------------------	------------------------

export default function WaitTimeConsultBook({ basic = false, hideIcon, color, info, badge, badgeType, hasPrioritizedMessage, label, siteStatus, light }) {

	const [show_time_modal, set_show_time_modal] = useState(false)
	const [show_scripts_modal, set_show_scripts_modal] = useState(false)
	const waitTimeExp = useExperiment(WAIT_TIME_EXP, { quiet: true });

	const { is_admin, is_supp, is_work } = app.acl || {}

	const wr_stat = siteStatus?.wr_stat
	const recently_active_doctors = siteStatus?.recently_active_doctors

	if (!wr_stat) return null

	const prediction_data = wr_stat?.prediction

	const call_98 = prediction_data?.data?.pc_98_wait
	const call_98_txt = call_98 && ((call_98 == mins_min && `in less than ${mins_to_hum_time(mins_min)}`) || (call_98 == mins_max && `in more than ${mins_to_hum_time(mins_max)}`) || `within ${mins_to_hum_time(call_98)}`) || 'within 1 hour'

	const avg_wait = prediction_data?.data?.pc_98_wait // Use 98 upper limit
	const avg_wait_req = siteStatus?.async_avg_wait;

	// Staff always see the real estimate as a basic badge; the wait_time experiment is patient-only
	if(!is_work && (waitTimeExp?.n !== "show_wt" || avg_wait > 30)) return null;


	const avg_wait_txt = avg_wait && (
		app.acl.is_work ? ((avg_wait == mins_min && `within ${mins_to_hum_time(mins_min)}`) || (avg_wait == mins_max && `more than ${mins_to_hum_time(mins_max)}`) || `within ${mins_to_hum_time(avg_wait)}`)
		: (avg_wait > 15 && avg_wait <= 30 && `${siteStatus.WR_wait?.default_avg_wait_mins||15} minutes`) || `~ ${mins_to_hum_time(avg_wait)}`
		) || '~ 1 hour';
	
	const avg_req_txt =  avg_wait_req && `~ ${avg_wait_req} mins`;
	const showIS2Message = app?.settings?.iscr2 ?? true
	const type = (basic || is_work) && "basic" || (avg_wait > 15) && "short" || "full";

	const waitTimeTextProps = {
		showIS2Message,
		avg_wait_txt,
		avg_req_txt,
		hasPrioritizedMessage,
		label,
		color,
		badgeType:type
	}
 
	return (
		<>
			<div className="relative">
				{(showIS2Message && badge) ?
					<>
					{type === "full" && <WaitTimeInfoModal {...{avg_wait, avg_wait_req, light}} />}
					<Alert
						className={`h-auto items-center !p-0 !mb-0 ${type === "full" ? info_notch : ""}`}
						bg={(hasPrioritizedMessage || app.acl.is_work) && color || "bg-transparent" }
						wait={true}
						content={<WaitTimeText wrStats={Boolean(info)} {...waitTimeTextProps} />}
						hideIcon
					/>
					</>
					:
					<Alert
						bg={color}
						wait={true}
						hideIcon={hideIcon}
						content={(info || !basic) &&
							<> {!basic && (<>
								<p className='text-sm'>98% of calls are made <strong>{call_98_txt}</strong>.
									<a className="flex justify-start underline text-sm cursor-pointer">
										<SIcon name="external" size="xxs" bold /> Learn more about estimated call back times</a></p>
							</>)}
								<p><WaitTimeText {...waitTimeTextProps} wrStats={Boolean(info)} /></p>
								<p>Recently active doctors: {recently_active_doctors}
									{(is_admin || is_supp) && (
										<a className="underline cursor-pointer ml-2"
											onClick={e => { e.stopPropagation(); set_show_scripts_modal(true) }}>
											Details</a>
									)}
								</p>
								{info && <p>{info}</p>}
							</>
						}
					/>}
			</div>

			<Modal
				open={show_time_modal}
				onClose={_ => set_show_time_modal(false)}
				closeIcon
			>
				<Modal.Content>
					<WaitTimeConsult />
				</Modal.Content>
			</Modal>

			<Modal
				open={show_scripts_modal}
				onClose={_ => set_show_scripts_modal(false)}
				closeIcon
				className="recent-docs-modal"
				style={{ width: '90% !important', height: '90% !important' }}
			>
				<style>{`
					.ui.modal.recent-docs-modal { width: 90% !important; max-width: 90% !important; height: 90% !important; margin: 0 !important; }
					.ui.modal.recent-docs-modal > .content { height: 100% !important; max-height: none !important; overflow-y: auto !important; }
				`}</style>
				<Modal.Content scrolling>
					{show_scripts_modal && <RecentDocsActivity />}
				</Modal.Content>
			</Modal>
		</>
	)
}
